import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import Button from '../components/Button'
import ButtonTertiary from '../components/ButtonTertiary'
import NeutralButton from '../components/NeutralButton'
import axios from 'axios'
import { API_BASE_URL } from '../config'
import { useAuth } from '../contexts/AuthContext'
import DefaultAlertLibrary from '../components/DefaultAlertLibrary'
import AlertWizard from '../components/AlertWizard'
import { Plus } from 'lucide-react'
import { fetchAlerts } from '../services/screenerApi'

// Memoized Alert Item Component to prevent re-rendering unchanged items
const AlertItem = React.memo(({ alert, getStatusColor, getTypeColor }) => {
  // Pre-compute values to avoid calling functions during render
  const statusColor = getStatusColor(alert.status)
  const typeColor = getTypeColor(alert.type)

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <span className="text-2xl mt-1">{alert.icon}</span>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-gray-900">{alert.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold ${statusColor}`}>
                {alert.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-gray-600 mt-2">
              <p>
                <span className="font-medium text-gray-500">Type:</span> <span className={typeColor}>{alert.type}</span>
              </p>
              <p>
                <span className="font-medium text-gray-500">Target:</span> {alert.target.type === 'watchlist' ? `Watchlist: ${alert.target.name}` : alert.target.name}
              </p>
              <p className="col-span-2">
                <span className="font-medium text-gray-500">Condition:</span> {alert.condition}
              </p>
              <p className="col-span-2">
                <span className="font-medium text-gray-500">Notifications:</span> {alert.notifications.join(', ')}
              </p>
            </div>

            {alert.lastTriggered && (
              <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded inline-block">
                Last triggered: <span className="font-medium text-gray-700">{alert.lastTriggered.time}</span>
                {alert.lastTriggered.stock && ` (${alert.lastTriggered.stock} ${alert.lastTriggered.details})`}
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-2 ml-4">
          <ButtonTertiary type="button">
            Edit
          </ButtonTertiary>
          <ButtonTertiary type="button">
            Pause
          </ButtonTertiary>
          <ButtonTertiary type="button" className="text-red-600 hover:bg-red-50 hover:border-red-200">
            Delete
          </ButtonTertiary>
        </div>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if alert data actually changed
  return (
    prevProps.alert.id === nextProps.alert.id &&
    prevProps.alert.name === nextProps.alert.name &&
    prevProps.alert.status === nextProps.alert.status &&
    prevProps.alert.type === nextProps.alert.type &&
    prevProps.alert.condition === nextProps.alert.condition &&
    JSON.stringify(prevProps.alert.target) === JSON.stringify(nextProps.alert.target) &&
    JSON.stringify(prevProps.alert.notifications) === JSON.stringify(nextProps.alert.notifications) &&
    JSON.stringify(prevProps.alert.lastTriggered) === JSON.stringify(nextProps.alert.lastTriggered)
  )
})

AlertItem.displayName = 'AlertItem'

const AlertPage = () => {
  // Debug: Track renders
  const renderCount = React.useRef(0)
  renderCount.current += 1
  console.log('🔄 AlertPage RENDER #', renderCount.current)

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [selectedDateRange, setSelectedDateRange] = useState('all')
  const [conversationHistory, setConversationHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { isAuthenticated } = useAuth()
  const [activeTab, setActiveTab] = useState('active') // 'active' | 'history'
  const [activeAlertFilter, setActiveAlertFilter] = useState('all')
  const [activeTriggerFilter, setActiveTriggerFilter] = useState('all')
  const [isLibraryOpen, setIsLibraryOpen] = useState(false)
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [alertsLoading, setAlertsLoading] = useState(true)
  const [alertsError, setAlertsError] = useState(null)

  // useEffect(() => {
  //   if (isAuthenticated) {
  //     fetchHistory()
  //   }
  // }, [isAuthenticated])

  // const fetchHistory = async () => {
  //   try {
  //     setIsLoading(true)
  //     const response = await axios.get(`${API_BASE_URL}/api/webapp/conversations/`)

  //     setConversationHistory(response.data.map(conv => ({
  //       id: conv.id,
  //       title: conv.user_query,
  //       date: conv.created_at,
  //       messages: 2, // 1 user query + 1 AI response
  //       page: "AI Chat",
  //       preview: conv.ai_response,
  //       tags: ["chat"],
  //       attachedTables: [],
  //       duration: "N/A",
  //       fullResponse: conv.ai_response // Store full response for display if needed
  //     })))
  //   } catch (error) {
  //     console.error('Failed to fetch conversation history:', error)
  //     setConversationHistory([])
  //   } finally {
  //     setIsLoading(false)
  //   }
  // }

  // const filters = [
  //   { id: 'all', label: 'All Conversations', icon: '📋' },
  //   { id: 'chat', label: 'AI Chat', icon: '💬' },
  //   { id: 'chart', label: 'Technical Chart', icon: '📈' },
  //   { id: 'screener', label: 'Stock Screener', icon: '🔍' },
  //   { id: 'flow', label: 'Flow Builder', icon: '🔄' }
  // ]

  // const dateRanges = [
  //   { id: 'all', label: 'All Time' },
  //   { id: 'today', label: 'Today' },
  //   { id: 'week', label: 'This Week' },
  //   { id: 'month', label: 'This Month' },
  //   { id: 'quarter', label: 'This Quarter' }
  // ]

  // const filteredConversations = () => {
  //   let filtered = conversationHistory

  //   // Filter by search query
  //   if (searchQuery) {
  //     filtered = filtered.filter(conv => 
  //       conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //       conv.preview.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //       conv.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  //     )
  //   }

  //   // Filter by page type
  //   if (selectedFilter !== 'all') {
  //     filtered = filtered.filter(conv => {
  //       const pageMap = {
  //         'chat': 'AI Chat',
  //         'chart': 'Technical Chart',
  //         'screener': 'Stock Screener',
  //         'flow': 'Flow Builder'
  //       }
  //       return conv.page === pageMap[selectedFilter]
  //     })
  //   }

  //   // Filter by date range
  //   if (selectedDateRange !== 'all') {
  //     const now = new Date()
  //     const convDate = new Date()

  //     filtered = filtered.filter(conv => {
  //       const convDate = new Date(conv.date)

  //       switch (selectedDateRange) {
  //         case 'today':
  //           return convDate.toDateString() === now.toDateString()
  //         case 'week':
  //           const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  //           return convDate >= weekAgo
  //         case 'month':
  //           const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  //           return convDate >= monthAgo
  //         case 'quarter':
  //           const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
  //           return convDate >= quarterAgo
  //         default:
  //           return true
  //       }
  //     })
  //   }

  //   return filtered.sort((a, b) => new Date(b.date) - new Date(a.date))
  // }

  // const formatDate = (dateString) => {
  //   const date = new Date(dateString)
  //   const now = new Date()
  //   const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

  //   if (diffInHours < 24) {
  //     return `${diffInHours} hours ago`
  //   } else if (diffInHours < 48) {
  //     return 'Yesterday'
  //   } else {
  //     return date.toLocaleDateString()
  //   }
  // }

  // const getPageIcon = (page) => {
  //   const iconMap = {
  //     'AI Chat': '💬',
  //     'Technical Chart': '📈',
  //     'Stock Screener': '🔍',
  //     'Flow Builder': '🔄'
  //   }
  //   return iconMap[page] || '📄'
  // }

  // if (isLoading) {
  //   return (
  //     <div className="flex h-full bg-gray-50 items-center justify-center">
  //       <div className="text-center">
  //         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
  //         <p className="text-gray-600 mt-4">Loading conversation history...</p>
  //       </div>
  //     </div>
  //   )
  // }

  // Alerts state - populated from API
  const [alerts, setAlerts] = useState([])

  // Memoize helper functions to prevent recreation on every render
  const getStatusColor = useCallback((status) => {
    return status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
  }, [])

  const getTypeColor = useCallback((type) => {
    switch (type) {
      case 'quantitative': return 'text-blue-600'
      case 'event': return 'text-purple-600'
      case 'content': return 'text-orange-600'
      default: return 'text-gray-600'
    }
  }, [])

  const getIconForType = useCallback((type) => {
    const iconMap = {
      'quantitative': '📊',
      'event': '📅',
      'technical_analysis': '📈',
      'content': '📰',
      'composition': '↔️'
    }
    return iconMap[type] || '🔔'
  }, [])

  const formatCondition = useCallback((alertData) => {
    if (alertData.type === 'event') {
      const events = alertData.condition?.events || []
      if (events.length === 0) {
        return 'No events selected'
      }
      // Map event values to readable labels
      const eventLabels = {
        'dividend_declaration': 'Dividend Declaration',
        'dividend_record_date': 'Dividend Record Date',
        'dividend_payment_date': 'Dividend Payment Date',
        'stock_split': 'Stock Split',
        'ipo': 'IPO',
        'economic_event': 'Economic Event',
        'earnings_announcement': 'Earnings Announcement',
        'merger_and_acquisition': 'Merger and Acquisition',
        'analyst_rating_change': 'Analyst Rating Change',
        'sec_filing': 'SEC Filing'
      }
      const formattedEvents = events.map(e => eventLabels[e] || e).join(', ')
      return formattedEvents
    }

    if (alertData.type === 'quantitative') {
      const { alertType, operator, value, parameter1, parameter2 } = alertData.condition || {}
      if (!alertType || !operator) {
        return 'Condition not set'
      }

      // Format operator for display
      const operatorLabels = {
        '>': 'Greater Than',
        '>=': 'Greater Than or Equal',
        '<': 'Less Than',
        '<=': 'Less Than or Equal',
        '=': 'Equal',
        'crossover': 'Crossover',
        'crossover_above': 'Crossover Above',
        'crossover_below': 'Crossover Below',
        'touch_upper': 'Touch Upper Band',
        'touch_lower': 'Touch Lower Band',
        'breakout_above': 'Breakout Above',
        'breakout_below': 'Breakout Below',
        'squeeze': 'Squeeze',
        'divergence_bullish': 'Bullish Divergence',
        'divergence_bearish': 'Bearish Divergence'
      }
      const formattedOperator = operatorLabels[operator] || operator

      // Build condition string based on alert type
      let conditionStr = `${alertType}: ${formattedOperator}`

      // Add value if present
      if (value !== undefined && value !== null && value !== '') {
        conditionStr += ` ${value}`
      }

      // Add parameter1 if present
      if (parameter1) {
        conditionStr += ` (${parameter1}`

        // Add parameter2 if present
        if (parameter2) {
          // Handle special format for Bollinger Bands (period,std_dev)
          if (parameter2.includes(',')) {
            const [period, stdDev] = parameter2.split(',')
            conditionStr += `, Period: ${period}, Std Dev: ${stdDev}`
          } else {
            conditionStr += `, ${parameter2}`
          }
        }
        conditionStr += ')'
      }

      return conditionStr
    }

    if (alertData.type === 'content') {
      const sources = alertData.condition?.sources || []
      if (sources.length === 0) {
        return 'No sources configured'
      }
      const sourceNames = sources.map(s => s.type || s.name || 'Unknown').join(', ')
      return `Monitoring: ${sourceNames}`
    }

    if (alertData.type === 'composition') {
      const changes = alertData.condition?.changes || []
      const notifyOnRebalance = alertData.condition?.notifyOnRebalance || false

      if (changes.length === 0) {
        return 'No changes selected'
      }

      const changeLabels = {
        'added': 'Stock Added',
        'removed': 'Stock Removed',
        'rebalanced': 'Weight Rebalanced'
      }
      const formattedChanges = changes.map(c => changeLabels[c] || c).join(', ')

      // Add weekly rebalancing info if enabled
      if (notifyOnRebalance) {
        return `${formattedChanges} | Weekly Rebalancing: Enabled`
      }

      return formattedChanges
    }

    if (alertData.type === 'technical_analysis') {
      const { patterns, chart_time_horizon, running_time_section, interval, user_prompt } = alertData.condition || {}

      if (!patterns || patterns.length === 0) {
        return 'No patterns selected'
      }

      // Map pattern values to readable labels
      const patternLabels = {
        'significant_reversal_pattern': 'Significant Reversal Pattern',
        'breakout': 'Breakout',
        'head_and_shoulders': 'Head & Shoulders',
        'reverse_head_and_shoulders': 'Reverse Head & Shoulders'
      }

      // Map chart time horizon values to readable labels
      const chartTimeHorizonLabels = {
        'minute': 'Minute',
        'daily': 'Daily',
        'weekly': 'Weekly',
        'monthly': 'Monthly'
      }

      // Map running time section values to readable labels
      const runningTimeLabels = {
        'during_market_hour': 'During Market Hour',
        'market_end': 'Market End'
      }

      // Format patterns
      const formattedPatterns = patterns.map(p => patternLabels[p] || p).join(', ')

      // Build condition string
      let conditionStr = `Patterns: ${formattedPatterns}`

      // Add chart time horizon
      if (chart_time_horizon) {
        const chartLabel = chartTimeHorizonLabels[chart_time_horizon] || chart_time_horizon
        conditionStr += ` | Chart: ${chartLabel}`
      }

      // Add running time section
      if (running_time_section) {
        const timeLabel = runningTimeLabels[running_time_section] || running_time_section
        conditionStr += ` | Time: ${timeLabel}`

        // Add interval if during market hour
        if (running_time_section === 'during_market_hour' && interval) {
          conditionStr += ` (Interval: ${interval})`
        }
      }

      // Add user prompt if provided
      if (user_prompt) {
        conditionStr += ` | Prompt: ${user_prompt}`
      }

      return conditionStr
    }

    return 'Condition not configured'
  }, [])

  // Transform API alert to frontend format - memoized to prevent recreation
  // Must be defined AFTER formatCondition and getIconForType
  const transformAlertFromAPI = useCallback((apiAlert) => {
    // Determine target
    let target = {}
    if (apiAlert.symbolgroup) {
      target = {
        type: 'watchlist',
        id: apiAlert.symbolgroup.id,
        name: apiAlert.symbolgroup.group_name
      }
    } else if (apiAlert.symbols && apiAlert.symbols.length > 0) {
      target = {
        type: 'stock',
        stocks: apiAlert.symbols.map(s => ({
          id: s.id,
          symbol: s.symbol,
          name: s.name,
          exchange_name: s.exchange_name
        }))
      }
      // For display, show first stock or count
      if (apiAlert.symbols.length === 1) {
        target.name = apiAlert.symbols[0].symbol
      } else {
        target.name = `${apiAlert.symbols.length} stocks`
      }
    } else {
      target = { type: 'watchlist', name: 'Unknown' }
    }

    // Determine condition based on alert type
    let condition = ''
    if (apiAlert.alert_category === 'quantitative' && apiAlert.alert_criteria) {
      const criteria = apiAlert.alert_criteria
      condition = formatCondition({
        type: 'quantitative',
        condition: {
          alertType: criteria.alertType,
          operator: criteria.operator,
          value: parseFloat(criteria.value) || criteria.value,
          parameter1: criteria.parameter1,
          parameter2: criteria.parameter2
        }
      })
    } else if (apiAlert.alert_category === 'event' && apiAlert.alert_criteria?.event_types) {
      condition = formatCondition({
        type: 'event',
        condition: {
          events: apiAlert.alert_criteria.event_types
        }
      })
    } else if (apiAlert.alert_category === 'technical_analysis' && apiAlert.alert_criteria) {
      const criteria = apiAlert.alert_criteria
      condition = formatCondition({
        type: 'technical_analysis',
        condition: {
          patterns: criteria.patterns || [],
          chart_time_horizon: criteria.chart_time_horizon,
          running_time_section: criteria.running_time_section,
          interval: criteria.interval_during_market_hour,
          user_prompt: criteria.user_prompt
        }
      })
    } else if (apiAlert.alert_category === 'screener' && apiAlert.alert_criteria) {
      const criteria = apiAlert.alert_criteria
      // Map backend event_types to frontend format
      const changes = (criteria.event_types || []).map(eventType => {
        const mapping = {
          'stock_added': 'added',
          'stock_removed': 'removed',
          'weight_rebalance': 'rebalanced'
        }
        return mapping[eventType] || eventType
      })
      condition = formatCondition({
        type: 'composition',
        condition: {
          changes: changes,
          notifyOnRebalance: criteria.include_weekly_rebalancing || false
        }
      })
    } else {
      condition = 'Condition not configured'
    }

    // Build notifications array
    const notifications = []
    if (apiAlert.email) notifications.push('Email')
    if (apiAlert.whatsapp) notifications.push('WhatsApp')

    // Format last triggered
    let lastTriggered = null
    if (apiAlert.last_triggered) {
      // You can format this timestamp as needed
      const date = new Date(apiAlert.last_triggered)
      const now = new Date()
      const diffMs = now - date
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffHours / 24)

      let timeStr = ''
      if (diffDays > 0) {
        timeStr = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      } else if (diffHours > 0) {
        timeStr = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else {
        const diffMins = Math.floor(diffMs / (1000 * 60))
        timeStr = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
      }

      lastTriggered = {
        time: timeStr,
        stock: null,
        details: null
      }
    }

    return {
      id: apiAlert.id,
      name: apiAlert.name,
      type: apiAlert.alert_category,
      icon: getIconForType(apiAlert.alert_category),
      target: target,
      condition: condition,
      notifications: notifications,
      status: apiAlert.is_active ? 'active' : 'paused',
      lastTriggered: lastTriggered
    }
  }, [getIconForType, formatCondition])

  // Mock data for triggers (extra entries to test scroller)
  const triggers = [
    {
      id: 1,
      date: 'Today, Dec 3, 2025',
      time: '2:35 PM',
      icon: '🔔',
      type: 'Price Alert Triggered',
      alertName: 'Tech Growth - 10% Gain Alert',
      stock: 'AAPL (+12.4%)',
      details: 'Price: $175.50 → $197.25',
      notifications: ['Email ✓', 'Telegram ✓']
    },
    {
      id: 2,
      date: 'Today, Dec 3, 2025',
      time: '11:20 AM',
      icon: '📰',
      type: 'News Alert Triggered',
      alertName: 'Tech Growth - News Monitoring',
      stock: null,
      details: 'Article: "Apple announces breakthrough in AI chip design"',
      source: 'TechCrunch',
      notifications: ['Telegram ✓']
    },
    {
      id: 3,
      date: 'Today, Dec 3, 2025',
      time: '10:15 AM',
      icon: '🔔',
      type: 'Price Alert Triggered',
      alertName: 'Bangladesh Commercial Banks - Quantitative Alert',
      stock: 'BRAC Bank (+5.2%)',
      details: 'price_deviation: Greater Than or Equal 2',
      notifications: ['Email ✓']
    },
    {
      id: 4,
      date: 'Today, Dec 3, 2025',
      time: '9:00 AM',
      icon: '↔️',
      type: 'Watchlist Changed',
      alertName: 'Bangladesh Commercial Bank - Composition Alert',
      stock: null,
      details: 'Watchlist rebalanced: 1 stock added, 0 removed',
      notifications: ['Email ✓', 'WhatsApp ✓']
    },
    {
      id: 5,
      date: 'Today, Dec 3, 2025',
      time: '8:30 AM',
      icon: '📰',
      type: 'News Alert Triggered',
      alertName: 'News Monitoring - Software Application Companies',
      stock: null,
      details: 'Article: "SaaS sector sees record funding in Q4"',
      source: 'Reuters',
      notifications: ['Email ✓']
    },
    {
      id: 6,
      date: 'Today, Dec 3, 2025',
      time: '8:00 AM',
      icon: '🔔',
      type: 'Price Alert Triggered',
      alertName: 'Twitter Monitoring - AAPL',
      stock: 'AAPL (+3.1%)',
      details: 'Price crossed above $190.00',
      notifications: ['Email ✓']
    },
    {
      id: 7,
      date: 'Yesterday, Dec 2, 2025',
      time: '3:45 PM',
      icon: '↔️',
      type: 'Watchlist Changed',
      alertName: null,
      stock: null,
      details: 'Tech Growth watchlist updated: +2 stocks added, -1 removed',
      notifications: ['Email ✓']
    },
    {
      id: 8,
      date: 'Yesterday, Dec 2, 2025',
      time: '2:20 PM',
      icon: '🔔',
      type: 'Price Alert Triggered',
      alertName: 'Dividend Watch - High Yield',
      stock: 'XYZ Corp (+8%)',
      details: 'Dividend yield crossed 4% threshold',
      notifications: ['Email ✓', 'Telegram ✓']
    },
    {
      id: 9,
      date: 'Yesterday, Dec 2, 2025',
      time: '1:00 PM',
      icon: '📰',
      type: 'News Alert Triggered',
      alertName: 'Sector News - Fintech',
      stock: null,
      details: 'Article: "Regulatory update impacts payment processors"',
      source: 'Bloomberg',
      notifications: ['Telegram ✓']
    },
    {
      id: 10,
      date: 'Yesterday, Dec 2, 2025',
      time: '11:30 AM',
      icon: '🔔',
      type: 'Price Alert Triggered',
      alertName: 'Volatility Alert - MSFT',
      stock: 'MSFT (-2.1%)',
      details: 'Intraday move exceeded 2%',
      notifications: ['Email ✓']
    },
    {
      id: 11,
      date: 'Yesterday, Dec 2, 2025',
      time: '10:00 AM',
      icon: '↔️',
      type: 'Watchlist Changed',
      alertName: 'Bangladesh Commercial Banks',
      stock: null,
      details: 'Composition change: 3 stocks rebalanced',
      notifications: ['Email ✓']
    },
    {
      id: 12,
      date: 'Yesterday, Dec 2, 2025',
      time: '9:15 AM',
      icon: '📰',
      type: 'News Alert Triggered',
      alertName: 'Earnings Monitor',
      stock: 'GOOGL',
      details: 'Earnings beat estimates; stock up in pre-market',
      source: 'CNBC',
      notifications: ['Email ✓', 'WhatsApp ✓']
    },
    {
      id: 13,
      date: 'Dec 1, 2025',
      time: '4:00 PM',
      icon: '🔔',
      type: 'Price Alert Triggered',
      alertName: 'Screener - Top Gainers',
      stock: 'NVDA (+6.5%)',
      details: 'Entered top 10 gainers list',
      notifications: ['Email ✓']
    },
    {
      id: 14,
      date: 'Dec 1, 2025',
      time: '12:00 PM',
      icon: '↔️',
      type: 'Watchlist Changed',
      alertName: null,
      stock: null,
      details: 'Weekly rebalance completed for Tech Growth',
      notifications: ['Email ✓']
    },
    {
      id: 15,
      date: 'Dec 1, 2025',
      time: '10:45 AM',
      icon: '📰',
      type: 'News Alert Triggered',
      alertName: 'IPO Alert',
      stock: null,
      details: 'New IPO filing: CloudSecure Inc.',
      source: 'SEC Filings',
      notifications: ['Telegram ✓']
    }
  ]

  // Memoize filteredAlerts to prevent recalculation on every render
  const filteredAlerts = useMemo(() => {
    return alerts.filter(alert => {
      // Apply status filter
      if (activeAlertFilter === 'active' && alert.status !== 'active') return false
      if (activeAlertFilter === 'paused' && alert.status !== 'paused') return false

      // Apply search query filter
      if (searchQuery && !alert.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false
      }

      return true
    })
  }, [alerts, activeAlertFilter, searchQuery])

  // Memoize filteredTriggers
  const filteredTriggers = useMemo(() => {
    return triggers.filter(trigger => {
      if (activeTriggerFilter === 'last24h') return trigger.date.includes('Today')
      return true
    })
  }, [activeTriggerFilter])

  // Fetch alerts from API - only runs once on mount
  useEffect(() => {
    const loadAlerts = async () => {
      setAlertsLoading(true)
      setAlertsError(null)
      try {
        const apiAlerts = await fetchAlerts()
        const transformedAlerts = apiAlerts.map(transformAlertFromAPI)
        setAlerts(transformedAlerts)
      } catch (err) {
        console.error('Failed to fetch alerts:', err)
        setAlertsError('Failed to load alerts')
        setAlerts([])
      } finally {
        setAlertsLoading(false)
      }
    }
    loadAlerts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty array - only run on mount. transformAlertFromAPI is stable via useCallback

  const handleApplyTemplate = useCallback((template) => {
    const newAlert = {
      id: Date.now(),
      name: template.title,
      type: template.type,
      icon: template.icon,
      target: { type: 'watchlist', name: 'Select Watchlist...' }, // Placeholder
      condition: template.description,
      notifications: ['Email'],
      status: 'active',
      lastTriggered: null
    }
    setAlerts(prevAlerts => [newAlert, ...prevAlerts])
    setIsLibraryOpen(false)
  }, [])

  // // Helper function to get icon for alert type
  // const getIconForType = (type) => {
  //   const iconMap = {
  //     'quantitative': '📊',
  //     'event': '📅',
  //     'content': '📰',
  //     'composition': '📋'
  //   }
  //   return iconMap[type] || '🔔'
  // }

  // // Helper function to format condition for display
  // const formatCondition = (alertData) => {
  //   const { type, condition, target } = alertData

  //   if (type === 'quantitative') {
  //     // Format quantitative alert condition
  //     if (condition?.parameter1 && condition?.operator) {
  //       const value = condition.value || condition.parameter2 || ''
  //       return `${condition.parameter1} ${condition.operator} ${value}`
  //     }
  //     return 'Price/Volume condition'
  //   } else if (type === 'event') {
  //     // Format event alert condition
  //     if (condition?.events && condition.events.length > 0) {
  //       return `Events: ${condition.events.join(', ')}`
  //     }
  //     return 'Corporate events'
  //   } else if (type === 'content') {
  //     // Format content alert condition
  //     if (condition?.source) {
  //       const sourceType = condition.source.source_type || 'news'
  //       if (sourceType === 'news') {
  //         return 'News monitoring'
  //       } else if (sourceType === 'youtube') {
  //         return `YouTube: ${condition.source.url || 'Channel'}`
  //       }
  //       return `Content: ${sourceType}`
  //     }
  //     return 'Content monitoring'
  //   } else if (type === 'composition') {
  //     return 'Watchlist composition changes'
  //   }

  //   return 'Alert condition'
  // }

  // const handleSaveAlert = (alertData) => {
  //   // Capitalize notification channel names
  //   const notificationLabels = {
  //     email: 'Email',
  //     telegram: 'Telegram',
  //     whatsapp: 'WhatsApp'


  const handleSaveAlert = useCallback(async (alertData) => {
    // After creating alert, refetch from API to get the latest data
    try {
      const apiAlerts = await fetchAlerts()
      const transformedAlerts = apiAlerts.map(transformAlertFromAPI)
      setAlerts(transformedAlerts)
    } catch (err) {
      console.error('Failed to refetch alerts after creation:', err)
      // Fallback: add to local state if API refetch fails
      const notificationLabels = {
        email: 'Email',
        telegram: 'Telegram',
        whatsapp: 'WhatsApp'
      }

      const newAlert = {
        id: Date.now(),
        name: alertData.notifications.name || 'New Alert',
        type: alertData.type,
        icon: getIconForType(alertData.type),
        target: alertData.target,
        condition: formatCondition(alertData),
        notifications: Object.keys(alertData.notifications)
          .filter(k => alertData.notifications[k] === true && notificationLabels[k])
          .map(k => notificationLabels[k]),
        status: 'active',
        lastTriggered: null
      }
      setAlerts(prevAlerts => [newAlert, ...prevAlerts])
    }
    setIsWizardOpen(false)
  }, [transformAlertFromAPI, getIconForType, formatCondition])

  // Memoized retry handler to prevent recreation
  const handleRetry = useCallback(async () => {
    setAlertsLoading(true)
    setAlertsError(null)
    try {
      const apiAlerts = await fetchAlerts()
      const transformedAlerts = apiAlerts.map(transformAlertFromAPI)
      setAlerts(transformedAlerts)
    } catch (err) {
      console.error('Failed to fetch alerts:', err)
      setAlertsError('Failed to load alerts')
    } finally {
      setAlertsLoading(false)
    }
  }, [transformAlertFromAPI])

  // Memoize all event handlers to prevent recreation
  const handleTabChangeToActive = useCallback(() => {
    setActiveTab('active')
  }, [])

  const handleTabChangeToHistory = useCallback(() => {
    setActiveTab('history')
  }, [])

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value)
  }, [])

  const handleAlertFilterAll = useCallback(() => {
    setActiveAlertFilter('all')
  }, [])

  const handleAlertFilterActive = useCallback(() => {
    setActiveAlertFilter('active')
  }, [])

  const handleAlertFilterPaused = useCallback(() => {
    setActiveAlertFilter('paused')
  }, [])

  const handleTriggerFilterAll = useCallback(() => {
    setActiveTriggerFilter('all')
  }, [])

  const handleTriggerFilterLast24h = useCallback(() => {
    setActiveTriggerFilter('last24h')
  }, [])

  const handleOpenLibrary = useCallback(() => {
    setIsLibraryOpen(true)
  }, [])

  const handleCloseLibrary = useCallback(() => {
    setIsLibraryOpen(false)
  }, [])

  const handleOpenWizard = useCallback(() => {
    setIsWizardOpen(true)
  }, [])

  const handleCloseWizard = useCallback(() => {
    setIsWizardOpen(false)
  }, [])

  return (
    <div className="flex h-full bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-8xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Alerts</h1>
            <p className="text-gray-600">Manage alerts and view triggered alert history</p>
          </div>

          {/* Tabs & Actions Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              {/* Tabs */}
              <div className="flex space-x-6">
                <button
                  type="button"
                  onClick={handleTabChangeToActive}
                  className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'active'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Active Alerts
                </button>
                <button
                  type="button"
                  onClick={handleTabChangeToHistory}
                  className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'history'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Trigger History
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                  />
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </span>
                </div>
                <Button
                  type="button"
                  onClick={handleOpenLibrary}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm hidden"
                >
                  Default Alerts
                </Button>
                <Button
                  type="button"
                  icon={Plus}
                  label="New Alert"
                  onClick={handleOpenWizard}
                />
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-0">
            {activeTab === 'active' ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Active Alerts Filters */}
                <div className="border-b border-gray-200 p-4 flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleAlertFilterAll}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeAlertFilter === 'all'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={handleAlertFilterActive}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeAlertFilter === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={handleAlertFilterPaused}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeAlertFilter === 'paused'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    Paused
                  </button>
                </div>

                {/* Alert List - scrollable so all alerts are visible */}
                <div className="max-h-[calc(100vh-18rem)] overflow-y-auto">
                  <div className="divide-y divide-gray-200">
                  {alertsLoading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Loading alerts...</p>
                    </div>
                  ) : alertsError ? (
                    <div className="p-8 text-center">
                      <p className="text-sm text-red-600">{alertsError}</p>
                      <button
                        type="button"
                        onClick={handleRetry}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                      >
                        Retry
                      </button>
                    </div>
                  ) : filteredAlerts.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p>No alerts found</p>
                    </div>
                  ) : (
                    filteredAlerts.map(alert => (
                      <AlertItem
                        key={alert.id}
                        alert={alert}
                        getStatusColor={getStatusColor}
                        getTypeColor={getTypeColor}
                      />
                    ))
                  )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Trigger History Filters */}
                <div className="border-b border-gray-200 p-4 flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleTriggerFilterAll}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTriggerFilter === 'all'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    All Triggers
                  </button>
                  <button
                    type="button"
                    onClick={handleTriggerFilterLast24h}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTriggerFilter === 'last24h'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    Last 24h
                  </button>
                </div>

                {/* Trigger Timeline - scrollable so all history is visible */}
                <div className="max-h-[calc(100vh-18rem)] overflow-y-auto">
                  <div className="p-6 space-y-6">
                  {filteredTriggers.map((trigger, idx) => (
                    <div key={trigger.id}>
                      {(idx === 0 || triggers[idx - 1]?.date !== trigger.date) && (
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{trigger.date}</h3>
                      )}

                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 relative ml-2">
                        <div className="absolute -left-2 top-6 w-2 h-2 bg-gray-300 rounded-full"></div>
                        <div className="flex items-start space-x-3">
                          <span className="text-xl">{trigger.icon}</span>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-gray-500">{trigger.time}</span>
                              <span className="text-sm font-semibold text-gray-900">{trigger.type}</span>
                            </div>

                            <div className="space-y-1 text-sm text-gray-600">
                              {trigger.alertName && <p><span className="font-medium">Alert:</span> {trigger.alertName}</p>}
                              {trigger.stock && <p><span className="font-medium">Stock:</span> {trigger.stock}</p>}
                              {trigger.source && <p><span className="font-medium">Source:</span> {trigger.source}</p>}
                              <p className="text-gray-800 mt-1">{trigger.details}</p>
                              <p className="text-xs text-green-600 mt-2 flex items-center">
                                <span className="mr-1">✓</span> Notifications sent: {trigger.notifications.join(', ')}
                              </p>
                            </div>
                          </div>

                          <ButtonTertiary type="button">
                            View Details
                          </ButtonTertiary>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="text-center pt-4">
                    <Button type="button" className="text-sm px-6 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
                      Load More History
                    </Button>
                  </div>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Default Alert Library Modal */}
      <DefaultAlertLibrary
        isOpen={isLibraryOpen}
        onClose={handleCloseLibrary}
        onApply={handleApplyTemplate}
      />

      {/* Alert Wizard Modal */}
      <AlertWizard
        isOpen={isWizardOpen}
        onClose={handleCloseWizard}
        onSave={handleSaveAlert}
      />

    </div>
  )
}

export default AlertPage 